import { useState, useEffect } from 'react';
import {FaEye, FaSave, FaUndo, FaPlay} from 'react-icons/fa';
import {IoIosAddCircleOutline, IoMdTrash, IoMdCreate} from 'react-icons/io';
import styles from './Pasos.module.css';
import VerPaso from './VerPaso';
import EditarPaso from './EditarPaso';
import AniadirPaso from './AniadirPaso';
import Swal from 'sweetalert2';
import imageDef from "./images/MIS_INTERESESFT.png";
import {useAuth} from '../../Auth/AuthContext'; 

const Paso = () => {
    const [pasos, setPasos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentView, setCurrentView] = useState('list'); // 'list', 'edit', 'add', 'view'
    const [editingPasoId, setEditingPasoId] = useState(null);
    const [viewingPaso, setViewingPaso] = useState(null);
    const [originalOrder, setOriginalOrder] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const {getToken} = useAuth(); 
    const token = getToken(); 

    // Funciones de drag and drop
    const handleDragStart = (e, paso, index) => {
        // No permitir arrastrar pasos inactivos
        if (!paso.pa_activo) {
            e.preventDefault();
            return;
        }

        setIsDragging(true);
        setDraggedItem({ paso, index });
        e.dataTransfer.effectAllowed = 'move';

        e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        setIsDragging(false);
        setDraggedItem(null);
        e.target.style.opacity = '1';

        document.querySelectorAll('.drag-over').forEach(el => {
            el.classList.remove('drag-over');
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        if (draggedItem && draggedItem.index !== index) {
            e.currentTarget.classList.add('drag-over');
        }
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-over');
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');

        if (!draggedItem || draggedItem.index === targetIndex) {
            return;
        }

        const newPasos = [...pasos];
        const draggedPaso = newPasos[draggedItem.index];

        // Remover el elemento de su posición original
        newPasos.splice(draggedItem.index, 1);

        // Insertarlo en la nueva posición
        newPasos.splice(targetIndex, 0, draggedPaso);

        // Actualizar los números de paso solo para pasos activos
        const updatedPasos = newPasos.map((paso, index) => ({
            ...paso,
            pa_numero: paso.pa_activo ? index + 1 : paso.pa_numero
        }));

        setPasos(updatedPasos);
        setHasChanges(true);
    };

    // Guardar nuevo orden
    const handleSaveOrder = async () => {
        try {
            const nuevoOrden = pasos.filter(paso => paso.pa_activo).map(paso => paso.pa_clave);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/reorder`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    nuevo_orden: nuevoOrden
                })
            });

            const data = await response.json();

            if (data.success) {
                setOriginalOrder([...pasos]);
                setHasChanges(false);
                Swal.fire({
                    title: 'Orden guardado correctamente', 
                    icon: 'success', 
                    timer: 1000,
                    showConfirmButton: false, 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                })
            } else {
                Swal.fire({
                    title: 'Error al guardar orden', 
                    icon: 'error', 
                    confirmButtonText: 'Entendido', 
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                })
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error de conexión');
        }
    };

    // Restaurar orden original
    const handleRestoreOrder = () => {
        setPasos([...originalOrder]);
        setHasChanges(false);
    };

    useEffect(() => {
        fetchPasos();
    }, []);

    const fetchPasos = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/pasos-con-estado`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const data = await response.json();
            setPasos(data.pasos);
            setOriginalOrder([...data.pasos]);
            setLoading(false);
        } catch (err) {
            console.error('Error:', err);
            setError('Error al cargar los pasos. Por favor, intenta de nuevo más tarde.');
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setCurrentView('add');
    };

    const handleEdit = (pasoId) => {
        setEditingPasoId(pasoId);
        setCurrentView('edit');
    };

    const handleBackToList = () => {
        setCurrentView('list');
        setEditingPasoId(null);
        setViewingPaso(null);
    };

    const handlePasoUpdated = () => {
        fetchPasos();
    };

    const handlePasoAdded = () => {
        fetchPasos();
    };

    const handleReactivate = async (pasoId, pasoTitulo) => {
        const confirmReactivate = await Swal.fire({
            title: '¿Reactivar paso?',
            text: `¿Estás seguro de que quieres reactivar "${pasoTitulo}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Reactivar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito', 
            },
        });

        if (!confirmReactivate.isConfirmed) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/reactivar-paso/${pasoId}`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `Error: ${response.status}`);
            }

            await fetchPasos();
            Swal.fire({
                title: 'Paso Reactivado',
                text: 'El paso ha sido reactivado exitosamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false, 
                customClass: {
                    popup: 'popupbonito', 
                },
            });
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo reactivar el paso',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57', 
                customClass: { 
                    popup: 'popupbonito', 
                },
            });
        }
    };

    const handleDelete = async (pasoId, paso) => {
        // Si el paso tiene usuarios con progreso, mostrar opción de desactivar
        if (paso.requiere_desactivacion) {
            const result = await Swal.fire({
                title: '¿Desactivar paso?',
                html: `
                    <p>Este paso tiene <strong>${paso.usuarios_con_progreso}</strong> usuario(s) con progreso.</p>
                    <p>No se puede eliminar completamente, pero se puede desactivar.</p>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Desactivar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#242b57', 
                customClass: { 
                    popup: 'popupbonito', 
                },
            });

            if (!result.isConfirmed) return;

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/eliminar-paso/${pasoId}`, {
                    method: 'DELETE',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                    }
                });

                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }

                await fetchPasos();
                Swal.fire({
                    title: 'Paso Desactivado',
                    text: 'El paso ha sido desactivado correctamente',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false, 
                    customClass: {
                        popup: 'popupbonito', 
                    },
                });
            } catch (error) {
                console.error('Error:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo desactivar el paso',
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                });
            }
        } else {
            // Eliminación completa si no hay usuarios con progreso
            const confirmDelete = await Swal.fire({
                title: '¿Eliminar paso?',
                text: 'Esta acción no se puede deshacer',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Eliminar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                },
            });

            if (!confirmDelete.isConfirmed) return;

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/eliminar-paso/${pasoId}`, {
                    method: 'DELETE',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                    }
                });

                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }

                await fetchPasos();
                Swal.fire({
                    title: 'Paso Eliminado',
                    text: 'El paso ha sido eliminado',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false, 
                    customClass: {
                        popup: 'popupbonito', 
                    },
                });
            } catch (error) {
                console.error('Error:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo eliminar el paso',
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                });
            }
        }
    };

    const handleView = (pasoId) => {
        const paso = pasos.find(t => t.pa_clave === pasoId);
        if (paso) {
            setViewingPaso(paso);
            setCurrentView('view');
        }
    };

    // Si estamos en vista de agregar, mostrar el componente AñadirPaso
    if (currentView === 'add') {
        return (
            <AniadirPaso
                onBack={handleBackToList}
                onPasoAdded={handlePasoAdded}
            />
        );
    }

    // Si estamos en vista de edición, mostrar el componente EditarPaso
    if (currentView === 'edit') {
        return (
            <EditarPaso
                pasoId={editingPasoId}
                onBack={handleBackToList}
                onPasoUpdated={handlePasoUpdated}
            />
        );
    }
    // Si estamos en vista de detalles, mostrar el componente VerPaso
    if (currentView === 'view') {
        return (
            <VerPaso
                paso={viewingPaso}
                onBack={handleBackToList}
            />
        );
    }

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando pasos...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorAlert} role="alert">
                <strong>Error: </strong>
                <span>{error}</span>
            </div>
            );
    }

    // Lista de pasos
    return (
        <div className={styles.pasosContainer}>
            <header className={styles.pasosHeader}>
                <h1>Gestión de Pasos</h1>
                <p>
                    Aquí encontrarás todos los pasos disponibles. Puedes encontrar las secciones dentro de la vista de cada paso.
                </p>
            </header>

            <div className={styles.addPasos}>
                <button className={styles.btnAddPasos} onClick={handleAdd}>
                    <IoIosAddCircleOutline />
                    Añadir Paso 
                </button>
            </div>
            {hasChanges && (
                <div className={styles.orderButtons}>
                    <button
                        className={styles.btnSaveOrder}
                        onClick={handleSaveOrder}
                        title="Guardar nuevo orden"
                    >
                        <FaSave /> Guardar Orden
                    </button>
                    <button
                        className={styles.btnRestoreOrder}
                        onClick={handleRestoreOrder}
                        title="Restaurar orden original"
                    >
                        <FaUndo /> Restaurar
                    </button>
                </div>
            )}

            {pasos.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3>No hay pasos disponibles</h3>
                    <p>Agrega un primer paso :D</p>
                </div>
            ) : (
                <div className={styles.pasosGroup}>
                    {pasos.map((paso, index) => (
                        <div
                            key={paso.pa_clave}
                            className={`${styles.pasosCard} ${isDragging ? styles.draggableActive : ''} ${!paso.pa_activo ? styles.inactivePaso : ''}`}
                            draggable={paso.pa_activo}
                            onDragStart={(e) => handleDragStart(e, paso, index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            style={{
                                opacity: paso.pa_activo ? 1 : 0.5,
                                cursor: paso.pa_activo ? 'grab' : 'default'
                            }}
                        >

                            <div className={styles.cardActions}>
                                
                                {paso.pa_activo ? (
                                    <>
                                        <button
                                            className={styles.btnPasoView}
                                            onClick={() => handleView(paso.pa_clave)}
                                            title="Ver detalles"
                                        >
                                            <FaEye/>
                                        </button>
                                        <button
                                            className={styles.btnPasoEdit}
                                            onClick={() => handleEdit(paso.pa_clave)}
                                            title="Editar paso"
                                        >
                                            <IoMdCreate />
                                        </button>
                                        <button
                                            className={styles.btnPasoDelete}
                                            onClick={() => handleDelete(paso.pa_clave, paso)}
                                            title={paso.puede_eliminar_completamente ? "Eliminar paso" : "Desactivar paso"}
                                        >
                                            <IoMdTrash />
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className={styles.btnPasoEdit}
                                        onClick={() => handleReactivate(paso.pa_clave, paso.pa_titulo)}
                                        title="Reactivar paso"
                                    >
                                        <FaPlay />
                                    </button>
                                )}
                            </div>

                            <div className={styles.cardIconPaso}>
                            <img
    src={
        paso.pa_imagen
            ? `${process.env.REACT_APP_API_URL}/uploads/${paso.pa_imagen}`
            : imageDef
    }
    alt={paso.pa_nombre || "Imagen paso"}
    style={{ 
        width: '100%', 
        maxWidth: '220px',     /* 📏 Controla el ancho dentro de la tarjeta */
        height: '130px',       /* 📏 Altura compacta y uniforme */
        objectFit: 'cover',    /* 🎨 Encaja perfecto sin deformar la foto */
        borderRadius: '20px',  /* 🔴 Bordes bien redondeados y estilizados */
        margin: '0 auto 12px auto',
        display: 'block'
    }}
    onError={(e) => {
        e.target.onerror = null;
        e.target.src = imageDef;
    }}
/>
                            </div>

                            <div className={styles.cardPasoContent}>
                                <h3 className={styles.cardPasoTitle}>
                                    Paso {paso.pa_numero}: {paso.pa_titulo}
                                    {!paso.pa_activo && <span className={styles.inactiveText}> (Inactivo)</span>}
                                </h3>
                                <div className={styles.cardPasoDetalles}>
                                    {paso.pa_detalles}
                                </div>
                                {paso.usuarios_con_progreso > 0 && (
                                    <div className={styles.cardPasoDetallesP}>
                                        {paso.usuarios_con_progreso} usuario(s) con progreso
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Paso;